import React, { useEffect, useState } from 'react';
import { createBrowserHistory } from 'history';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import AuthLayout from 'layouts/Auth.js';
import DashboardLayout from 'layouts/Dashboard';
import ErrorPage from 'views/ErrorPage';
import Auth from 'components/Auth/Auth';
import RTL from 'components/RTL/RTL';
import CustomLoadingIndicator from 'components/CustomLoadingIndicator/CustomLoadingIndicator';
import Toast from 'components/Toast/Toast';
import Cookies from 'components/Cookies/Cookies';
import Register from 'layouts/Register';
import LockScreenManager from 'components/LockScreenManager/LockScreenManager';
import styles from 'assets/jss/material-dashboard-pro-react/components/appStyle';
import AdminMain from 'views/AdminMain';
import { setRemoteConfig } from 'redux/modules/settings/remoteConfig/actions';
import { useDispatch } from 'react-redux';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import { IGuestLogIn } from 'utils/api/models/eshop/guestLogIn';
import { EshopProductsService } from 'services/eshop/products';
import { updateHeaders } from 'redux/modules/settings/headers/actions';
import { throwError } from 'rxjs';
import { useSelector } from 'react-redux';
import { isAuthenticating, isLoggedInSelector } from 'redux/modules/session/selectors';
import idConfig from 'utils/ecommerce/services/id.config';
import { TagManager } from 'commons/gtm/tagManager';
import { env as E } from 'config/app.config';
import { UsersService } from 'services/users/users';
import { UserProfile } from 'utils/api/models/users/userProfile';
import { getLanguage } from 'utils/language/language';
import { fetchLabels } from 'utils/labels/labels';
import { SecurityService } from 'services/sequrityServices/securityServices';
import { setNmfloId } from './redux/modules/session/actions';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isVoc } from 'utils/killswitch';
import { loadQualtricsScript } from 'utils/qualtrics/snippets';
import _ from 'lodash';
import { finalize } from 'rxjs/operators';
import { featuresActive } from 'config/app.config';
import GetConfigService from 'services/config/config';
import { setWCMAppConfig } from 'redux/modules/settings/appConfig/actions';

const hist = createBrowserHistory();

// @ts-ignore
const useStyles = makeStyles(styles);

const App = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const [configLoaded, setConfigLoaded] = useState(false);
	const isLogged = useSelector(isLoggedInSelector);
	const remoteConfig = useSelector(remoteConfigSelector);
	const authenticating = useSelector(isAuthenticating);
	const isVocOn = isVoc(remoteConfig);

	useEffect(() => {
		setConfigLoaded(false);
		const tagManagerArgs = {
			gtmId: (idConfig.GTM as any)[E] || '',
		};
		fetchLabels(getLanguage());
		TagManager.initialize(tagManagerArgs);

		GetConfigService.getConfigurations()
			.pipe(
				finalize(() => {
					setConfigLoaded(true);
				})
			)
			.subscribe(
				({ response }) => {
					const isVoc = response.featureFlags?.some((obj: types.FeatureFlag) => {
						const [key, value] = Object.values(obj);
						return key === 'voc' && value;
					});
					if (isVoc) {
						loadQualtricsScript();
					}

					const featureFlags = (response.featureFlags ?? []).reduce(
						(acc: Record<string, boolean>, curr: types.FeatureFlag) => {
							acc[curr.key] = curr.value;
							return acc;
						},
						{} as Record<string, boolean>
					);
					dispatch(setRemoteConfig({ sidebar: featureFlags }));
					dispatch(
						setWCMAppConfig({
							appConfig: response.appConfig,
							permissions: response.permissions?.content ?? [],
							domain: response.domain.content,
						})
					);
				},
				() => {
					dispatch(setRemoteConfig(featuresActive));
				}
			);

		/* TODO: comment code for full release 		
		forkJoin([
			RemoteConfigService.getRemoteConfig(),
			NewDashboardConfig.getWhitelistedCRNumber(),
			AccountsService.getCorpAccountsForCurrentUser(),
		]).subscribe({
			next: (response) => {
				const remoteConfigs = response[0];
				const accounts = response[2]?.accounts;
				const whiteListedAccounts = response[1];

				const isNewHeaderOn = remoteConfigs?.sidebar?.enableNewHeader;
				const enabledNewHeader =
					!whiteListedAccounts.length ||
					_.intersection(
						accounts?.map((acc) => acc?.documentId?.toString()),
						whiteListedAccounts.map((acc) => acc?.toString())
					)?.length > 0;

				dispatch(setRemoteConfig(remoteConfigs));
				dispatch(setNewHeaderFooterEnabled(isNewHeaderOn && enabledNewHeader));
				dispatch(setSelectedAccountDetails(accounts));
				dispatch(setAllAccountDetails(accounts));

				if (remoteConfigs?.sidebar?.voc) {
					loadQualtricsScript();
				}

				setConfigLoaded(true);
			},
			error: (err) => {
				console.error('Error occurred:', err);
				setConfigLoaded(true);
			},
		}); */
	}, [isLogged]);

	useEffect(() => {
		if (isLogged) {
			EshopProductsService.getOmantelIdentity().subscribe(
				(data: IGuestLogIn) => {
					dispatch(
						updateHeaders({
							WCTrustedToken: data?.WCTrustedToken,
							WCToken: data?.WCToken,
							WCPersonalization: data?.personalizationID,
						})
					);
				},
				(error: any) => {
					throwError(error);
				}
			);

			UsersService.getUserProfile().subscribe((response: UserProfile) => {
				if (response?.upId) {
					TagManager.dataLayer({
						dataLayer: {
							event: 'login',
							userId: response.upId,
						},
					});
				}
			});
		}
	}, [dispatch, isLogged]);

	useEffect(() => {
		if (isLogged && isVocOn) {
			SecurityService.getQualtricsToken().subscribe(({ response }) => {
				if (response?.data?.nmfloId) {
					dispatch(setNmfloId(response?.data?.nmfloId));
				}
			});
		}
	}, [isVocOn, isLogged]);

	return (
		<>
			<LockScreenManager />
			<RTL>
				<>
					<Toast />
					<ConditionalRender
						show={configLoaded && !authenticating}
						onTrue={() => (
							<Router history={hist}>
								<Auth>
									<>
										<CustomLoadingIndicator />
										<Cookies />
										<Switch>
											<Route path="/auth" render={(props: any) => <AuthLayout {...props} />} />
											<Route path="/register" render={(props: any) => <Register {...props} />} />
											<Route path="/401" render={() => <h1>No permissions</h1>} />
											<Route
												path="/dashboard"
												render={(props: any) => <DashboardLayout {...props} />}
											/>
											<Route
												path="/shortcuts"
												render={(props: any) => <AdminMain {...props} />}
											/>
											<Route
												path="/error-page"
												render={(props: any) => <ErrorPage {...props} />}
											/>
											<Redirect exact from="/" to="/dashboard" />
											<Redirect to="/error-page" />
										</Switch>
									</>
								</Auth>
							</Router>
						)}
						onFalse={() => (
							<div className={classes.loadingWrapper}>
								<CustomCircularProgress
									text={translate('loading.it.can.take.a.while')}
									loading
									transparent
								/>
							</div>
						)}
					/>
				</>
			</RTL>
		</>
	);
};

export default App;
